import React, { useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { TextField, MenuItem, Grid } from '@material-ui/core';
import { Body1, LocationPermissionsTitleSpan, TabCard } from '../blocks';
import Snackbar from '../../Snackbar';
import { Body, PrimaryButton, SecondaryButton } from '@/styles/blocks';
import { getTariffName } from '../Tariffs/utils';
import { OnwardToggle } from '../../ShipmentForm/blocks';
import { colors } from '@/styles';
import ProfileImageUpload from '../ProfileImageUpload';
import StickyFooter from '../../StickyFooter';
import NavResponsiveModal from '@/components/Navigation/NavResponsiveModal';
import { ModalContent, ModalHeader, ModalActions } from '@/components/Navigation/NavResponsiveModal';
import { useMutation, useQuery } from '@apollo/client';
import { UPDATE_USER } from '../mutations';
import { toFullState } from '@/utilities/address';
import { GET_SHIPPER_LOAD_FEEDBACK_QUESTIONS } from './graphql';
import { RESPONSE_TYPES } from '@/components/Account/Surveys/constants';

export const EditShippingPartner = ({
    shipper,
    tariffs,
    overrides,
    callbacks,
    disabled,
    notification,
    shipperLabels,
}) => {
    const [overrideUpdates, updateOverride] = useState({});
    const [alias, setAlias] = useState(null);
    const [partnershipProfileImageVariables, setPartnershipProfileImageVariables] = useState(null);
    const [shareManifests, setShareManifests] = useState(shipper?.share_manifests);
    const [newShipperLabelOption, setNewShipperLabelOption] = useState(null);
    const [newShipperLabelSelection, setNewShipperLabelSelection] = useState(undefined);

    const [newShipperLabelModalOpen, setNewShipperLabelModalOpen] = useState(false);
    const [newShipperLabelModalText, setNewShipperLabelModalText] = useState('');

    const [updateUser, { loading: updateUserLoading }] = useMutation(UPDATE_USER);

    const { data: loadFeedbackData } = useQuery(GET_SHIPPER_LOAD_FEEDBACK_QUESTIONS, {
        variables: { client_id: shipper?.shipper?.client_id },
        skip: !shipper?.shipper?.client_id,
    });

    const loadFeedbackQuestions = loadFeedbackData?.survey_questions || [];

    const toggleFlag = (flag) => {
        const circles = shipper?.shipper?.user?.circles || {};
        const next = {
            ...(circles || {}),
            [flag]: !circles?.[flag],
        };
        updateUser({ variables: { user_id: shipper.shipper.client_id, update: { circles: next } } });
    };

    const tariffsById = useMemo(() => {
        return Object.fromEntries((tariffs || []).map((tariff) => [tariff.tariff_id, tariff]));
    }, [tariffs]);

    const override = useMemo(() => {
        return overrides.find((o) => o.oms && o.partner_client_id === shipper.shipper_id) || {};
    }, [overrides, shipper]);

    const updatedOverride = useMemo(() => {
        return {
            tariff_id: overrideUpdates.tariff_id || override.tariff_id,
            add_fuel_surcharge: Boolean(overrideUpdates.add_fuel_surcharge ?? override.add_fuel_surcharge),
        };
    }, [override, overrideUpdates]);

    const shipperName = useMemo(() => {
        return alias || shipper.shipper_alias || shipper.shipper.business_name;
    }, [alias, shipper]);

    const tariff = useMemo(() => {
        return tariffsById[updatedOverride.tariff_id];
    }, [tariffsById, updatedOverride]);

    const modified =
        Object.keys(overrideUpdates).length > 0 ||
        !!alias ||
        newShipperLabelSelection !== undefined ||
        shipper?.share_manifests !== shareManifests;

    const statusColors = {
        PENDING: colors.golds.primary,
        APPROVED: colors.greens.primary,
        DENIED: colors.reds.primary,
    };

    const statusLabels = {
        PENDING: 'Invite Pending',
        APPROVED: 'Connected',
        DENIED: 'Not Connected',
    };

    const allShipperLabels = useMemo(() => {
        return [...shipperLabels, ...(newShipperLabelOption ? [newShipperLabelOption] : [])];
    }, [shipperLabels, newShipperLabelOption]);

    return (
        <>
            <TabCard>
                <Snackbar
                    open={notification.message}
                    handleClose={() => callbacks.setNotification({})}
                    {...notification}
                />
                <Grid
                    container
                    direction="column"
                    css={css`
                        padding: 1rem 2rem;
                        border-bottom: 1px solid ${colors.greys.border};
                    `}
                >
                    <LocationPermissionsTitleSpan>Partnership Info</LocationPermissionsTitleSpan>
                    <Grid
                        item
                        css={css`
                            display: flex;
                            justify-content: flex-start;
                            align-items: center;
                            margin-bottom: 4px;
                            margin-top: 1rem;
                        `}
                    >
                        <Body
                            css={css`
                                font-weight: 700;
                                font-size: 14px;
                                color: ${statusColors[shipper.status] || 'black'};
                                width: 150px;
                            `}
                        >
                            {statusLabels[shipper.status] || 'Status Unknown'}
                        </Body>
                        <Body
                            css={css`
                                font-weight: 500;
                                font-size: 14px;
                                color: #8d8d8d;
                            `}
                        >
                            Network ID: {shipper.shipper.client_id}
                        </Body>
                    </Grid>
                    <Grid
                        item
                        css={css`
                            display: flex;
                            justify-content: flex-start;
                            align-items: center;
                            height: 74px;
                        `}
                    >
                        <Grid
                            item
                            css={css`
                                width: 400px;
                                margin-right: 12px;
                            `}
                        >
                            <TextField
                                variant="outlined"
                                label="Shipper Name"
                                value={shipperName}
                                onChange={(e) => setAlias(e.target.value)}
                                fullWidth
                            />
                        </Grid>
                    </Grid>
                    <Body>
                        Address:{' '}
                        {shipper?.shipper?.business_address
                            ? [
                                  shipper.shipper.business_address,
                                  shipper.shipper.business_address_unit,
                                  shipper.shipper.business_city,
                                  toFullState(shipper.shipper.business_state),
                                  shipper.shipper.business_zip,
                              ]
                                  .filter(Boolean)
                                  .join(', ')
                            : 'Not provided'}
                    </Body>
                    <Body>Email: {shipper?.shipper?.email ? shipper.shipper.email : 'Not provided'}</Body>
                    <Body>
                        Phone: {shipper?.shipper?.business_phone ? shipper.shipper.business_phone : 'Not provided'}
                    </Body>
                    <Grid
                        item
                        css={css`
                            display: flex;
                            justify-content: flex-start;
                            align-items: center;
                            height: 74px;
                        `}
                    >
                        <OnwardToggle
                            value={shareManifests}
                            onChange={(e) => setShareManifests(() => e.target.checked)}
                        />
                        <Body1
                            css={css`
                                margin-left: 12px;
                            `}
                        >
                            Share warehouse manifest information with this shipper.
                        </Body1>
                    </Grid>
                </Grid>
            </TabCard>
            <TabCard
                css={css`
                    margin-top: 2rem;
                `}
            >
                <Grid
                    container
                    direction="column"
                    css={css`
                        padding: 1rem 2rem;
                        border-bottom: 1px solid ${colors.greys.border};
                    `}
                >
                    <LocationPermissionsTitleSpan>Communication Preferences</LocationPermissionsTitleSpan>
                    <Grid
                        item
                        css={css`
                            display: flex;
                            justify-content: flex-start;
                            align-items: center;
                            height: 74px;
                        `}
                    >
                        <OnwardToggle
                            value={shipper?.shipper?.user?.circles?.['tms-order-completed-email']}
                            onChange={() => toggleFlag('tms-order-completed-email')}
                            disabled={shipper.status === 'APPROVED' || updateUserLoading}
                        />
                        <Body1
                            css={css`
                                margin-left: 12px;
                            `}
                        >
                            Email this shipper when an order of theirs is completed.
                        </Body1>
                    </Grid>
                    {shipper.status === 'APPROVED' && (
                        <Grid>
                            <Body1
                                css={css`
                                    margin-left: 12px;
                                    color: ${colors.reds.primary};
                                `}
                            >
                                Shipper must update their notification preferences via their account settings
                            </Body1>
                        </Grid>
                    )}
                </Grid>
            </TabCard>
            <TabCard
                css={css`
                    margin-top: 2rem;
                `}
            >
                <Grid
                    container
                    direction="column"
                    css={css`
                        padding: 1rem 2rem;
                        border-bottom: 1px solid ${colors.greys.border};
                    `}
                >
                    <LocationPermissionsTitleSpan>Shipper Label</LocationPermissionsTitleSpan>
                    <Grid
                        item
                        css={css`
                            width: 300px;
                            margin-top: 1rem;
                        `}
                    >
                        <TextField
                            select
                            variant="outlined"
                            label="Shipper Label"
                            value={
                                newShipperLabelSelection !== undefined
                                    ? newShipperLabelSelection
                                    : shipper.shipper_label || ''
                            }
                            onChange={(e) => setNewShipperLabelSelection(e.target.value)}
                            fullWidth
                        >
                            <MenuItem value={''}>None</MenuItem>
                            {allShipperLabels.map((label, idx) => {
                                return (
                                    <MenuItem key={`label-${idx}`} value={label}>
                                        {label}
                                    </MenuItem>
                                );
                            })}
                        </TextField>
                    </Grid>
                    <Grid
                        item
                        css={css`
                            display: flex;
                            align-items: center;
                            flex-wrap: nowrap;
                            justify-content: flex-start;
                            margin-top: 1rem;
                        `}
                    >
                        <PrimaryButton
                            onClick={() => {
                                setNewShipperLabelModalOpen(true);
                            }}
                        >
                            Create New Shipper Label
                        </PrimaryButton>
                    </Grid>
                </Grid>
            </TabCard>
            <ProfileImageUpload
                partnership={shipper}
                partnershipProfileImageVariables={partnershipProfileImageVariables}
                setPartnershipProfileImageVariables={setPartnershipProfileImageVariables}
            />
            <TabCard
                css={css`
                    margin-top: 2rem;
                `}
            >
                <Grid
                    container
                    direction="column"
                    css={css`
                        padding: 1rem 2rem;
                        border-bottom: 1px solid ${colors.greys.border};
                    `}
                >
                    <LocationPermissionsTitleSpan>Tariff</LocationPermissionsTitleSpan>
                    <Grid
                        item
                        css={css`
                            width: 300px;
                            margin-top: 1rem;
                        `}
                    >
                        <TextField
                            select
                            variant="outlined"
                            label="Tariff"
                            value={updatedOverride?.tariff_id || ''}
                            onChange={(e) => updateOverride((prev) => ({ ...prev, tariff_id: e.target.value }))}
                            fullWidth
                        >
                            {tariffs.map((tariff, idx) => (
                                <MenuItem key={tariff.tariff_id} value={tariff.tariff_id}>
                                    {getTariffName(tariff, idx)}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid
                        item
                        css={css`
                            display: flex;
                            align-items: center;
                            flex-wrap: nowrap;
                            justify-content: flex-start;
                            margin-top: 1rem;
                        `}
                    >
                        <OnwardToggle
                            value={updatedOverride.add_fuel_surcharge}
                            onChange={(e) =>
                                updateOverride((prev) => ({ ...prev, add_fuel_surcharge: e.target.checked }))
                            }
                        />
                        <Body1
                            css={css`
                                margin-left: 12px;
                            `}
                        >
                            Add Fuel Surcharge
                        </Body1>
                    </Grid>
                </Grid>
            </TabCard>
            {loadFeedbackQuestions.length > 0 && (
                <TabCard
                    css={css`
                        margin-top: 2rem;
                    `}
                >
                    <Grid
                        container
                        direction="column"
                        css={css`
                            padding: 1rem 2rem;
                            border-bottom: 1px solid ${colors.greys.border};
                        `}
                    >
                        <LocationPermissionsTitleSpan>Inbound Load Survey</LocationPermissionsTitleSpan>
                        {loadFeedbackQuestions.map((q, idx) => (
                            <Grid
                                item
                                key={q.survey_id}
                                css={css`
                                    margin-top: 1rem;
                                `}
                            >
                                <Body>
                                    {idx + 1}. {q.question}
                                </Body>
                                <Body1
                                    css={css`
                                        color: ${colors.greys.primary};
                                    `}
                                >
                                    {RESPONSE_TYPES[q.response_type] || q.response_type}
                                </Body1>
                            </Grid>
                        ))}
                    </Grid>
                </TabCard>
            )}
            <StickyFooter
                styles={{
                    paddingLeft: '-10px',
                    boxShadow: '0 -2px 5px rgba(0, 0, 0, 0.25)',
                }}
            >
                <Grid
                    container
                    direction="row"
                    css={css`
                        justify-content: space-between;
                    `}
                >
                    <Grid item>
                        <SecondaryButton
                            css={css`
                                margin-left: 15px;
                            `}
                            onClick={() => callbacks.setEditingShipperId(null)}
                        >
                            Back
                        </SecondaryButton>
                    </Grid>

                    <Grid item>
                        <PrimaryButton
                            onClick={() =>
                                callbacks
                                    .onSave(
                                        {
                                            pricing_override_id: override.pricing_override_id,
                                            partner_client_id: shipper.shipper.client_id,
                                            tariff_id: tariff?.tariff_id || null,
                                            algo_type: tariff?.algo_type || 'DEFAULT',
                                            add_fuel_surcharge: !!updatedOverride.add_fuel_surcharge,
                                        },
                                        {
                                            partnership_id: shipper.partnership_id,
                                            shipper_alias: shipperName,
                                            share_manifests: shareManifests,
                                            shipper_label:
                                                newShipperLabelSelection !== undefined
                                                    ? newShipperLabelSelection
                                                    : shipper.shipper_label,
                                            ...partnershipProfileImageVariables,
                                        }
                                    )
                                    .then(() => {
                                        updateOverride({});
                                        setNewShipperLabelSelection(undefined);
                                        setNewShipperLabelOption(null);
                                    })
                            }
                            disabled={!modified}
                        >
                            Save
                        </PrimaryButton>
                    </Grid>
                </Grid>
            </StickyFooter>
            {newShipperLabelModalOpen && (
                <NavResponsiveModal open={newShipperLabelModalOpen} onClose={() => setNewShipperLabelModalOpen(false)}>
                    <ModalHeader title="Create New Shipper Label" onClose={() => setNewShipperLabelModalOpen(false)} />
                    <ModalContent
                        css={css`
                            width: 350px;
                            height: 100px;
                        `}
                    >
                        <TextField
                            variant="outlined"
                            label="Shipper Label"
                            value={newShipperLabelModalText}
                            onChange={(e) => setNewShipperLabelModalText(e.target.value)}
                            fullWidth
                        />
                    </ModalContent>
                    <ModalActions>
                        <SecondaryButton
                            onClick={() => {
                                setNewShipperLabelModalOpen(false);
                                setNewShipperLabelModalText('');
                            }}
                        >
                            Cancel
                        </SecondaryButton>
                        <PrimaryButton
                            onClick={() => {
                                setNewShipperLabelModalOpen(false);
                                setNewShipperLabelOption(newShipperLabelModalText);
                                setNewShipperLabelSelection(newShipperLabelModalText);
                                setNewShipperLabelModalText('');
                            }}
                        >
                            Create
                        </PrimaryButton>
                    </ModalActions>
                </NavResponsiveModal>
            )}
        </>
    );
};
